import { glMatrix, vec2 } from "gl-matrix"
import { PubSub } from "@/utils/pub-sub"
import { Viewport } from "@/core/viewport"
import { debounced } from "@/utils/debounce"
import { Renderer } from "@/core/pipeline/renderer"
import { Adjustments, EditorEvent, Mask, OutputColorSpace, ViewMode } from "@/core/types"
import { WebGLBackend } from "@/core/backends/webgl-backend"
import { Photo } from "@/core/photo"
import { IOAdapter, IOAdapterOptions } from "./io-adapter"
import { AssetResolver } from "./asset-resolver"
import { ColorSamplePurpose } from "./color-sampler"
import { BrushStroke } from "./utils/brushing"
import { uuid } from "@/utils/uuid"
import { Texture } from "./backends/webgl/texture"
import { getPerspectiveMatrix } from "./perspective"
import { getDefaultAdjustments } from "./adjustments"

// Use normal arrays instead of typed arrays
glMatrix.setMatrixArrayType(Array)

export interface RenderCallOptions {
	reason?: string
	isDownsampled?: boolean
	isForced?: boolean
}

export class Editor {
	backend: WebGLBackend
	renderer: Renderer

	mainViewport: Viewport
	comparisonViewport: Viewport | null = null

	previewPhoto?: Photo
	originalPreviewPhoto?: Photo

	originalPhoto: Photo

	viewMode: ViewMode = "after"
	events = new PubSub<EditorEvent>()

	ioAdapters: IOAdapter[]

	maskOverlayIds: Set<string> | null = null
	maskOverlayPreviewMasks: Mask[] | null = null

	private waitingSync = false
	private renderPending = false
	private importPending = false

	private lastAdapterName = ""

	constructor(
		options: {
			label?: string
			gl?: WebGL2RenderingContext | null
			isHistogramEnabled?: boolean | null
			resolvers?: AssetResolver[]
			io?: IOAdapter[]
		} = {},
	) {
		this.backend = new WebGLBackend(options.gl)
		this.mainViewport = new Viewport(this.backend)

		this.renderer = new Renderer(this.backend, {
			label: options.label,
			resolvers: options.resolvers,
			isHistogramEnabled: options.isHistogramEnabled,
		})

		this.ioAdapters = options.io ?? []

		// this.photo = this.renderer.photo
		this.originalPhoto = this.renderer.photo
	}

	get photo() {
		return this.renderer.photo
	}

	get canvas() {
		return this.backend.canvas
	}

	get shaders() {
		return this.backend.shaders
	}

	/**
	 * Viewport params
	 */

	get imageScale() {
		return this.mainViewport.imageScale
	}

	get croppedImageScale() {
		return this.mainViewport.croppedImageScale
	}

	get zoom() {
		return this.mainViewport.croppedImageScale
	}

	get offset() {
		return this.mainViewport.offset
	}

	/**
	 * Image params
	 */

	get adjustments() {
		return this.photo.adjustments
	}

	get whiteBalance() {
		return this.photo.whiteBalance
	}

	get imageSize() {
		return this.photo.imageState.imageSize
	}

	get originalImageSize() {
		return this.photo.imageState.originalImageSize
	}

	get croppedImageSize() {
		return this.photo.imageState.croppedImageSize
	}

	get cropMatrix() {
		return this.photo.imageState.cropMatrix
	}

	get crop() {
		return this.photo.imageState.crop
	}

	get initialOrientation() {
		return this.photo.initialOrientation
	}

	get orientation() {
		return this.photo.initialOrientation + this.photo.adjustments.orientation
	}

	get rotation() {
		return (
			this.photo.initialOrientation * 90 +
			this.photo.adjustments.orientation * 90 +
			this.photo.adjustments.cropStraighten
		)
	}

	get healingCurrentStroke() {
		return this.renderer.healingPass.healingCurrentStroke
	}

	set healingCurrentStroke(strokes: BrushStroke | null) {
		this.renderer.healingPass.healingCurrentStroke = strokes

		if (!strokes) {
			return
		}

		this.render({ reason: "set-healing-stroke", isDownsampled: true })
		this.renderWithDebounce()
	}

	set enableHealing(isEnabled: boolean) {
		this.renderer.healingPass.enabled = isEnabled
		this.render({ reason: "enable-healing-changed", isDownsampled: true })
	}

	get isImportPending() {
		return this.importPending
	}

	setImportPending() {
		this.importPending = true
		this.originalPhoto = new Photo({ type: "bitmap", texture: this.renderer.scratchTexture })
	}

	async import(file: File, options: IOAdapterOptions) {
		this.renderer.performanceCounters.reset()

		for (const adapter of this.ioAdapters) {
			if (adapter.canLoad(file)) {
				if (this.lastAdapterName != adapter.getName()) {
					// On adapter switch we do a little bit of a cleanup
					this.renderer.cleanupDenoiseTextures()
				}

				this.lastAdapterName = adapter.getName()

				this.importPending = true

				// Clearing all assets cache for previous photo
				this.renderer.assets.cleanup()

				// Clearing healing strokes
				this.renderer.healingPass.healingCurrentStroke = null

				await adapter.load(file, this.renderer, options)

				await this.renderer.assets.resolveAll(this.photo)

				this.originalPhoto = this.photo.clone({})

				this.previewPhoto = undefined

				this.importPending = false

				if (!options.skipRenderOnLoad) {
					this.render({ reason: "import-full-size" })
				}

				if (!options.skipHistogram) {
					this.renderer.histogram.update(this.photo)
				}

				return
			}
		}

		console.warn("No matching IO adapter was found to open", file.name)
	}

	startSamplingColor(purpose: ColorSamplePurpose) {
		this.render({ reason: "start-sampling-color" })
		this.renderer.colorSampler.startSampling(purpose)
	}

	stopSamplingColor() {
		this.renderer.colorSampler.stopSampling()
		this.render({ reason: "stop-sampling-color" })
	}

	async sampleColor(position: vec2) {
		return this.renderer.colorSampler.sampleThrottled(position)
	}

	clearViewport() {
		this.renderer.previewTexture.update({
			data: null,
		})

		this.previewPhoto = undefined

		this.renderer.photo = new Photo({
			type: "bitmap",
			texture: this.renderer.previewTexture,
		})

		this.mainViewport.setTexture(null)

		this.backend.setFramebuffer(null)
		this.backend.clear()
	}

	setViewMode(viewMode: ViewMode) {
		if (viewMode === this.viewMode) {
			return
		}

		this.viewMode = viewMode

		this.mainViewport.setCropView(viewMode === "crop")

		if (this.viewMode === "compare") {
			this.comparisonViewport = new Viewport(this.backend)
			this.comparisonViewport.setViewportRect(0, 0, 0.5, 1)
			this.mainViewport.setViewportRect(0.5, 0, 0.5, 1)
		} else {
			this.comparisonViewport = null
			this.mainViewport.setViewportRect(0, 0, 1, 1)
		}

		this.render({ reason: "set-view-mode" })
	}

	toggleColorClipping(
		highlightsClipping: boolean,
		shadowsClipping: boolean,
		needRender: boolean = true,
	) {
		if (
			this.renderer.histogram.showsHighlightsClipping !== highlightsClipping ||
			this.renderer.histogram.showsShadowsClipping !== shadowsClipping
		) {
			this.renderer.histogram.setColorClipping(highlightsClipping, shadowsClipping)
			if (needRender) {
				this.render({ reason: "toggle-color-clipping" })
			}
		}
	}

	setAdjustments(adjustments: Partial<Adjustments>) {
		this.photo.adjustments = { ...this.photo.adjustments, ...adjustments }
		this.originalPhoto.adjustments = { ...this.photo.adjustments, ...adjustments }

		if (this.renderer.assets.shouldResolveArtefactsForAdjustments(this.photo.adjustments)) {
			this.renderer.assets.resolveAll(this.photo).then(() => {
				this.render({ reason: "set-adjustments-after-resolve" })
			})
		} else {
			this.render({ reason: "set-adjustments", isDownsampled: true })
			this.renderWithDebounce()
		}
	}

	setViewport(state: {
		offsetX?: number
		offsetY?: number
		zoom?: number | "fit"
		relativeZoom?: number
	}) {
		if (state.offsetX != null) {
			this.mainViewport.offset[0] = state.offsetX
		}

		if (state.offsetY != null) {
			this.mainViewport.offset[1] = state.offsetY
		}

		if (state.relativeZoom != null) {
			this.mainViewport.relativeZoom = state.relativeZoom
		}

		if (state.zoom != null) {
			if (state.zoom === "fit") {
				this.mainViewport.relativeZoom = 1
			} else {
				this.mainViewport.relativeZoom = state.zoom / this.mainViewport.croppedFitScale
			}
		}

		this.renderViewport()
		this.renderWithDebounce()
	}

	setCanvasSize(width: number, height: number) {
		this.backend.setCanvasSize(width, height)

		if (width <= 0 || height <= 0) {
			console.warn("Trying to set empty canvas size.")
			return
		}

		this.renderViewport()
		this.renderWithDebounce()
	}

	setOverlays(
		maskIds: typeof this.maskOverlayIds,
		previewMasks: typeof this.maskOverlayPreviewMasks = null,
	) {
		this.maskOverlayIds = maskIds
		this.maskOverlayPreviewMasks = previewMasks
		this.render({ reason: "set-overlays" })
	}

	public render = (options: RenderCallOptions = {}) => {
		this.renderPending = true

		if (this.waitingSync) {
			return
		}

		if (this.importPending && !options.isForced) {
			console.log(
				`[editor:${this.renderer.label}] Discarded render call while importing (reason=${options.reason})`,
			)
			return
		}

		if (options.reason) {
			console.log(`[editor:${this.renderer.label}] Render call (reason=${options.reason})`)
		} else {
			console.warn(
				`[editor:${this.renderer.label}] Render call with no specified reason is not allowed`,
			)
		}

		this.renderOutput(options)
		this.renderPending = false
		this.waitingSync = true

		this.backend.waitClientSync(() => {
			if (this.renderPending && !this.importPending) {
				this.renderOutput(options)
				this.renderPending = false

				// Wait for client sync again after rendering
				this.backend.waitClientSync(() => (this.waitingSync = false))
			} else {
				this.renderPending = false
				this.waitingSync = false
			}
		})
	}

	public renderPreview(options: {
		image: TexImageSource & { width: number; height: number }
		imageColorSpace?: OutputColorSpace
		originalImage?: TexImageSource & { width: number; height: number }
		originalImageAdjustments?: Adjustments
	}) {
		const { image, originalImage } = options
		const imageColorSpace = options.imageColorSpace ?? OutputColorSpace.displayP3

		// NOTE @din: We do not render preview in crop mode because we need full photo to be ready to properly calculate cropping dimensions
		// This means in crop mode we will wait for full photo to load
		// In the future we might actually have separate cropped vs uncropped previews to make it way faster (instant) in crop mode, but
		// for now this fixes the original issue which is
		// https://app.asana.com/1/1113185975437517/inbox/1113240832886425/item/1210691813523449/story/1210700160385960
		if (this.viewMode === "crop") {
			return
		}

		/* Current preview */

		if (image && image.width > 0 && image.height > 0) {
			this.renderer.previewTexture.update({
				width: image.width,
				height: image.height,
				data: image,
			})

			this.previewPhoto = new Photo({
				id: uuid(),
				type: "bitmap",
				texture: this.renderer.previewTexture,
				originalImageSize: [image.width, image.height],
			})

			this.previewPhoto.computeState()

			this.mainViewport.setTexture(this.renderer.previewTexture)
			this.mainViewport.render(this.previewPhoto.imageState, (texture) => {
				this.renderViewportTexture(texture, imageColorSpace)
			})
		}

		/* Splitview preview */

		if (
			this.viewMode === "compare" &&
			originalImage &&
			originalImage.width > 0 &&
			originalImage.height > 0
		) {
			this.renderer.scratchTexture.update({
				width: originalImage.width,
				height: originalImage.height,
				data: originalImage,
				format: "rgba8unorm",
			})

			this.renderer.originalPreviewTexture.update({
				width: originalImage.width,
				height: originalImage.height,
				data: originalImage,
				format: "rgba8unorm",
			})

			this.originalPreviewPhoto = new Photo({
				id: uuid(),
				type: "bitmap",
				texture: this.renderer.scratchTexture,
				adjustments: options.originalImageAdjustments,
				originalImageSize: [originalImage.width, originalImage.height],
			})

			this.originalPreviewPhoto.computeState()

			this.renderer.scratchTexture.bind(0)
			this.renderer.originalPreviewTexture.bindFramebuffer()

			const perspectiveMatrix = getPerspectiveMatrix(
				getDefaultAdjustments(this.originalPreviewPhoto),
				this.originalPreviewPhoto.initialOrientation,
				this.originalPreviewPhoto.imageState.originalImageSize,
			)

			this.shaders.cropPerspectiveOutput.render({
				inputTexture: 0,
				cropMatrix: this.originalPreviewPhoto.imageState.cropMatrixInv,
				perspectiveMatrix,
			})

			this.comparisonViewport?.setTexture(this.renderer.originalPreviewTexture)
			this.comparisonViewport?.render(this.originalPreviewPhoto.imageState, (texture) => {
				this.renderViewportTexture(texture, OutputColorSpace.displayP3)
			})
		}
	}

	@debounced(100)
	private renderWithDebounce() {
		this.render({ reason: "render-with-debounce" })
	}

	private renderViewportTexture = (viewport: Viewport, colorSpace?: OutputColorSpace) => {
		if (!viewport.texture) {
			return
		}

		const viewMatrix = this.viewMode === "crop" ? viewport.cropViewMatrix : viewport.viewMatrix

		viewport.texture?.bind(0)

		this.backend.shaders.viewport.render({
			viewMatrix: viewMatrix,
			inputTexture: 0,
			inputColorSpace: colorSpace ?? OutputColorSpace.displayP3,
			filterNearest: this.mainViewport.croppedImageScale > 2,
		})
	}

	private renderComparisonViewportTexture = (viewport: Viewport) => {
		if (!viewport.texture) {
			return
		}

		const viewMatrix = this.mainViewport.viewMatrix

		viewport.texture?.bind(0)

		this.backend.shaders.viewport.render({
			viewMatrix: viewMatrix,
			inputTexture: 0,
			filterNearest: this.mainViewport.croppedImageScale > 2,
		})
	}

	private renderViewport() {
		if (this.canvas.width == 0 || this.canvas.height == 0) {
			return
		}

		const currentPhoto = this.previewPhoto ?? this.photo

		currentPhoto.computeState()

		this.mainViewport.render(currentPhoto.imageState, this.renderViewportTexture)

		this.renderComparisonViewport()

		this.events.publish({ type: "render" })
	}

	private renderComparisonViewport(texture?: Texture) {
		if (this.canvas.width == 0 || this.canvas.height == 0) {
			return
		}

		const currentPhoto = this.originalPreviewPhoto ?? this.photo

		if (this.comparisonViewport) {
			if (texture) {
				this.comparisonViewport?.setTexture(texture)
			}

			// Synchronize state with main viewport
			this.comparisonViewport.relativeZoom = this.mainViewport.relativeZoom
			this.comparisonViewport.offset = this.mainViewport.offset
			this.comparisonViewport.render(
				currentPhoto.imageState,
				this.renderComparisonViewportTexture,
			)
		}
	}

	private renderOutput(options: RenderCallOptions = {}) {
		const currentTexture = this.photo.texture

		if (!options) {
			console.warn("[editor] Rendering output directly is not allowed")
		}

		if (!currentTexture.width || !currentTexture.height) {
			// Texture not yet loaded
			return
		}

		// Determine how large the image is in the viewport and render to that size

		this.photo.computeState()
		this.mainViewport.computeState(this.photo.imageState)

		let ignoreCrop: boolean
		let imageSize: vec2
		let imageScale: number

		if (this.viewMode === "crop") {
			ignoreCrop = true
			imageSize = this.imageSize
			imageScale = this.imageScale
		} else {
			ignoreCrop = false
			imageSize = this.croppedImageSize
			imageScale = this.croppedImageScale
		}

		let width = Math.min(imageSize[0] * imageScale, imageSize[0])
		let height = Math.min(imageSize[1] * imageScale, imageSize[1])

		if (options.isDownsampled) {
			const maxWidth = Math.max(imageSize[0] >> 1, 2048)
			const maxHeight = Math.max(imageSize[1] >> 1, 2048)
			const scale = Math.min(maxWidth / width, maxHeight / height, 1)

			width *= scale
			height *= scale
		}

		const texture = this.renderer.render(this.photo, {
			width,
			height,
			ignoreCrop,
			maskOverlayIds: this.maskOverlayIds,
			maskOverlayPreviewMasks: this.maskOverlayPreviewMasks,
			showHighlightsClipping: this.renderer.histogram.showsHighlightsClipping,
			showShadowsClipping: this.renderer.histogram.showsShadowsClipping,
			enableDither: true,
		})

		this.mainViewport.setTexture(texture)

		if (this.viewMode === "before" || this.viewMode === "compare") {
			this.originalPhoto.computeState()

			this.renderer.originalInputTexture.update({
				format: this.renderer.backend.textureFormat,
				width,
				height,
			})

			const originalTexture = this.renderer.renderWithoutAdjustments(this.originalPhoto, {
				width,
				height,
				output: { format: "rgba", texture: this.renderer.originalInputTexture },
			})

			if (this.viewMode === "compare") {
				if (!this.importPending) {
					this.comparisonViewport?.setTexture(originalTexture)
				}
			} else {
				this.mainViewport.setTexture(originalTexture)
			}
		}

		this.renderViewport()

		if (this.previewPhoto) {
			// Recompute the state after rendering the preview photo to the viewport to get the correct image size.
			this.mainViewport.computeState(this.originalPhoto.imageState)
		}

		this.renderer.healingSourceDetector.run()

		if (options.isDownsampled) {
			this.renderer.histogram.update(this.photo)
		}
	}
}
